// Builds the vue-flow graph (nodes + edges) for the workflow designer from the
// existing workflow `steps` structure. The data model is unchanged; we only add
// an optional `step.position` for the free-form layout (falling back to the
// legacy column/order grid when absent).
//
// Connectors originate from the SPECIFIC block inside a step (a logic-transition
// or action-invoke-flow block) — the edge's sourceHandle is that block's id, and
// StepNode renders a handle aligned to that block's row.

const COL_W = 400;
const ROW_H = 240;
const EXT_DX = 360; // external endpoint offset to the right of its source step
const EXT_DY = 120;

function isStart(step) {
  return step.id === '1' || step.name === 'Start';
}

function nodePosition(step) {
  if (step.position && typeof step.position.x === 'number' && typeof step.position.y === 'number') {
    return { x: step.position.x, y: step.position.y };
  }
  return { x: (step.column || 0) * COL_W, y: (step.order || 0) * ROW_H };
}

// Recursively collect outgoing links from a step's evaluate blocks. Each link
// records the originating block id (which matches the DOM id FlowStepList renders)
// so the edge can leave from that exact row.
function collectLinks(evaluate, out) {
  if (!evaluate) return;
  evaluate.forEach((item) => {
    if (item.type === 'logic-transition') {
      // use the block's own (unique) id — payload.id can be duplicated across blocks
      out.push({ blockId: item.id, kind: item.payload.type, value: item.payload.value });
    } else if (item.type === 'action-invoke-flow') {
      out.push({ blockId: item.id, kind: 'invoke', value: item.payload && item.payload.flow_id });
    } else if (item.type === 'logic-condition') {
      (item.payload.expressions || []).forEach((e) => collectLinks(e.payload, out));
      collectLinks(item.payload.else, out);
    } else if (item.type === 'logic-parallel') {
      collectLinks(item.payload, out);
    }
  });
}

function edge(id, source, sourceHandle, target, targetHandle, klass) {
  return { id, source, sourceHandle, target, targetHandle, type: 'default', animated: true, class: klass || 'wf-edge' };
}

export default function buildWorkflowGraph(steps = [], flowsMap = {}, extPositions = {}) {
  const nodes = [];
  const edges = [];
  const stepById = {};
  steps.forEach((s) => {
    stepById[s.id] = s;
  });
  const extIndex = {};

  steps.forEach((step) => {
    const p = nodePosition(step);

    if (isStart(step)) {
      nodes.push({ id: step.id, type: 'start', position: p, data: { name: step.name || 'Start' }, draggable: false, selectable: true });
      if (step.transition && stepById[step.transition]) {
        edges.push(edge('e-start', step.id, 'start-out', step.transition, 'in', 'wf-edge'));
      }
      return;
    }

    const links = [];
    collectLinks(step.evaluate, links);
    const portIds = [];

    links.forEach((lnk) => {
      if (lnk.kind === 'repeat') return; // "this step" loop — not drawn as a connector

      if (lnk.kind === 'step') {
        if (lnk.value && stepById[lnk.value]) {
          portIds.push(lnk.blockId);
          edges.push(edge(`e-${lnk.blockId}`, step.id, lnk.blockId, lnk.value, 'in', 'wf-edge'));
        }
        return;
      }

      // external endpoints: agent handoff / invoke flow / end
      portIds.push(lnk.blockId);
      const extId = `ext-${lnk.blockId}`;
      extIndex[step.id] = (extIndex[step.id] || 0) + 1;
      const idx = extIndex[step.id];
      let label;
      let icon;
      if (lnk.kind === 'end') {
        label = 'End';
        icon = 'square';
      } else if (lnk.kind === 'agent') {
        label = flowsMap[lnk.value] ? `Handoff: ${flowsMap[lnk.value]}` : 'Handoff: Agent / Flow';
        icon = 'memory';
      } else {
        label = flowsMap[lnk.value] ? `Invoke: ${flowsMap[lnk.value]}` : 'Invoke: Workflow';
        icon = 'account_tree';
      }
      nodes.push({
        id: extId,
        type: 'external',
        // dragged position wins; otherwise an initial guess that the parent then
        // aligns to the originating block's row once it's measured.
        position: extPositions[extId] || { x: p.x + EXT_DX, y: p.y + (idx - 1) * EXT_DY },
        data: { label, icon, kind: lnk.kind, sourceStepId: step.id },
        draggable: true,
        selectable: true,
      });
      edges.push(edge(`e-${lnk.blockId}`, step.id, lnk.blockId, extId, 'in', `wf-edge wf-edge--${lnk.kind}`));
    });

    nodes.push({
      id: step.id,
      type: 'step',
      position: p,
      data: { step, steps, portIds },
      draggable: true,
      selectable: true,
    });
  });

  return { nodes, edges };
}
